import { inject, Injectable } from '@angular/core';
import { BaseService } from './base.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { filter, map, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { some } from 'lodash-es';
import { User } from '@models/user';
import { ColorsThemeService } from './colors-theme.service';
import { LOCAL_STORAGE } from '@services/storage.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService extends BaseService {

  private userSubject: BehaviorSubject<User | null>;

  public user: Observable<User | null>;

  localStorage = inject(LOCAL_STORAGE);
  constructor(private router: Router,
              private colorThemeService: ColorsThemeService,
              http: HttpClient) {
    super(http);

    this.userSubject = new BehaviorSubject<User | null>(null);
    this.user = this.userSubject.asObservable();
  }

  public get loggedUser(): User | null {
    return this.userSubject.value;
  }

  public clearUser() {
    this.userSubject.next(null);
    this.router.navigateByUrl('/login');
  }

  public set loggedUser(value: User | null) {
    this.userSubject.next(value);
  }

  public hasOnlyRole(role: any) {
    return this.loggedUser && this.loggedUser.roles.length === 1 && this.loggedUser.roles[0] === role;
  }

  public hasRole(role: string) {
    return this.loggedUser && this.loggedUser.roles.indexOf(role) > -1;
  }

  public hasUserRole(user: User, role: string) {
    return user && user.roles.indexOf(role) > -1;
  }

  public hasAnyRole(roles: string[]) {
    return some(roles, r => this.hasRole(r));
  }

  register(request: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}public/register`, request, {
      headers: new HttpHeaders({'Content-Type': 'application/json'})
    }).pipe(map(result => this.handleUser(result)));
  }

  login(email: string, password: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}public/login`, {
      email: email,
      password: password,
    }, {
      headers: new HttpHeaders({'Content-Type': 'application/json'})
    }).pipe(map(result => this.handleUser(result)));
  }

  googleLogin(code: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}public/google-login?code=${code}`, {}, {
      headers: new HttpHeaders({'Content-Type': 'application/json'})
    }).pipe(map(result => this.handleUser(result)));
  }

  getLoggedUser(oneTimeToken: string | null = null): Observable<any> {
    let options = oneTimeToken ? {params: {otlt: oneTimeToken}} : {};
    return this.http.get<any>(`${this.baseUrl}public/authenticated`, options)
        .pipe(
          filter(result => !!result),
          map(result => this.handleUser(result))
        );
  }

  requestResetPassword(email: string): Observable<any> {
    return this.http.get(`${this.baseUrl}public/send-reset-password-email?email=${email}`);
  }

  changePassword(credentials: any, token: string, isLogged: boolean): Observable<any> {
    if (isLogged) {
      return this.http.post<any>(`${this.baseUrl}user/change-password`, credentials);
    } else {
      return this.http.post<any>(`${this.baseUrl}public/change-password?token=${token}`, credentials)
          .pipe(map(result => this.handleUser(result)));
    }
  }


  logout() {
    return this.http.post<any>(`${this.baseUrl}public/refresh/logout`, {})
      .pipe(take(1)).subscribe({
        next: value => {
          this.userSubject.next(value);
          this.router.navigateByUrl('/auth/login');
          this.localStorage.clear();
        },
        error: () => {}
      });
  }

  refresh(): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}public/refresh`, {})
        .pipe(map(result => this.handleUser(result)));
  }

  public handleUser(result: any) {
    const user = new User(result);
    user.id = result.userId;
    // localStorage.setItem(GlobalConstants.GROUP_ID_KEY, String(user.organizationId));
    this.userSubject.next(user);
    // localStorage.setItem('currentUser', JSON.stringify(user));
    return user;
  }

  impersonate(email: string) {

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-Impersonate': email,
      // Add any other headers if needed
    });
    return this.http.get(`${this.baseUrl}impersonate`, {headers});
  }

  impersonateLogout() {
    return this.http.get<any>(`${this.baseUrl}impersonate/logout`, {})
      .pipe(take(1)).subscribe({
        next: value => {
          if (value) {
            this.userSubject.next(value);
            this.router.navigate(['/admin/users']);
          } else {
            this.userSubject.next(value);
            this.router.navigateByUrl('/auth/login');
            this.localStorage.clear();
          }
        },
        error: () => {}
      });
  }
}
